// src/admin/pages/AdminPaymentLogs.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Table, Form, Button, InputGroup, Card, Dropdown, Badge, Spinner } from 'react-bootstrap';
import { Search, Calendar, Download, Eye, MoreVertical, ShieldAlert } from 'lucide-react';

import { getPaymentLogs } from '../../services/adminApi'; 

import './AdminPaymentsLog.css';

const AdminPaymentLogs = () => {
    const navigate = useNavigate();
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('All Statuses');
    const [gatewayFilter, setGatewayFilter] = useState('All Gateways');
    const [dateRange, setDateRange] = useState('');

    const fetchLogs = async () => {
        setLoading(true);
        try {
            const params = {
                search: search,
                status: statusFilter === 'All Statuses' ? undefined : statusFilter,
                gateway: gatewayFilter === 'All Gateways' ? undefined : gatewayFilter
            };
            
            // 🟢 यहाँ सीधे getPaymentLogs फ़ंक्शन का उपयोग किया गया है
            const response = await getPaymentLogs(params);
            setLogs(response.data.data || []);
        } catch (err) {
            console.error('Could not load payment logs.', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const delayDebounce = setTimeout(() => {
            fetchLogs();
        }, 300);
        return () => clearTimeout(delayDebounce);
    }, [search, statusFilter, gatewayFilter]);

    const getGatewayBadge = (provider) => {
        const cleanProvider = (provider || '').toLowerCase();
        if (cleanProvider.includes('razorpay')) {
            return <span className="d-inline-flex align-items-center gap-1 text-white bg-dark px-2 py-1 rounded border border-secondary small font-monospace">Razorpay <Badge bg="primary" style={{fontSize: '10px'}}>RP</Badge></span>;
        }
        if (cleanProvider.includes('phonepe')) {
            return <span className="d-inline-flex align-items-center gap-1 text-white bg-dark px-2 py-1 rounded border border-secondary small font-monospace">PhonePe <Badge bg="secondary" style={{fontSize: '10px', backgroundColor: '#5f259f'}}>PP</Badge></span>;
        }
        return <span className="admin-payments-text-secondary small">{provider || '-'}</span>;
    };

    return (
        <div className="container-fluid py-4 admin-payments-container">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h2 className="admin-payments-title">Payment Transactions</h2>
                    <p className="admin-payments-subtitle">Review and audit booking payment status logs</p>
                </div>
                <Badge bg="danger" className="px-3 py-2 fs-6 rounded-pill">Total logs: {logs.length}</Badge>
            </div>

            <Card className="border-0 rounded-4 overflow-hidden mb-4 admin-payments-card">
                <Card.Body className="p-4 bg-transparent admin-payments-border-bottom">
                    <div className="row g-3">
                        <div className="col-lg-4 col-md-6">
                            <InputGroup className="border-0 rounded-3 admin-payments-input-group">
                                <InputGroup.Text className="bg-transparent border-0 text-secondary">
                                    <Search size={18} />
                                </InputGroup.Text>
                                <Form.Control
                                    type="text"
                                    placeholder="Search by User, Payment ID, Order ID..."
                                    className="bg-transparent border-0 shadow-none ps-0 admin-payments-input"
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                />
                            </InputGroup>
                        </div>
                        <div className="col-lg-2 col-md-3">
                            <Form.Select className="border-0 rounded-3 text-white py-2 admin-payments-select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                                <option value="All Statuses">All Statuses</option>
                                <option value="SUCCESS">SUCCESS</option>
                                <option value="BOOKED">BOOKED</option>
                                <option value="PENDING">PENDING</option>
                                <option value="FAILED">FAILED</option>
                            </Form.Select>
                        </div>
                        <div className="col-lg-2 col-md-3">
                            <Form.Select className="border-0 rounded-3 text-white py-2 admin-payments-select" value={gatewayFilter} onChange={(e) => setGatewayFilter(e.target.value)}>
                                <option value="All Gateways">All Gateways</option>
                                <option value="Razorpay">Razorpay</option>
                                <option value="PhonePe">PhonePe</option>
                            </Form.Select>
                        </div>
                        <div className="col-lg-2 col-md-6">
                            <InputGroup className="border-0 rounded-3 admin-payments-input-group">
                                <InputGroup.Text className="bg-transparent border-0 text-secondary">
                                    <Calendar size={18} />
                                </InputGroup.Text>
                                <Form.Control
                                    type="text"
                                    placeholder="Select Date Range"
                                    className="bg-transparent border-0 shadow-none ps-0 admin-payments-input"
                                    value={dateRange}
                                    onChange={(e) => setDateRange(e.target.value)}
                                    disabled
                                />
                            </InputGroup>
                        </div>
                        <div className="col-lg-2 col-md-6 text-end">
                            <Button variant="outline-secondary" className="admin-payments-btn-secondary" onClick={() => window.print()}>
                                <Download size={18} /> Export / Print
                            </Button>
                        </div>
                    </div>
                </Card.Body>

                <div className="table-responsive">
                    <Table hover align="middle" className="m-0 text-nowrap table-dark table-borderless admin-payments-table">
                        <thead className="admin-payments-thead">
                            <tr className="admin-payments-thead-tr">
                                <th className="px-4 py-3 text-uppercase">#</th>
                                <th className="py-3 text-uppercase">Application ID</th>
                                <th className="py-3 text-uppercase">User Name</th>
                                <th className="py-3 text-uppercase">Email</th>
                                <th className="py-3 text-uppercase">Gateway</th>
                                <th className="py-3 text-uppercase">Order ID</th>
                                <th className="py-3 text-uppercase">Payment ID</th>
                                <th className="py-3 text-uppercase">Amount</th>
                                <th className="py-3 text-uppercase">Status</th>
                                <th className="py-3 text-uppercase">Date & Time</th>
                                <th className="px-4 py-3 text-center text-uppercase">Action</th>
                            </tr>
                        </thead>
                        <tbody style={{ fontSize: '0.9rem' }}>
                            {loading ? (
                                <tr>
                                    <td colSpan="11" className="text-center p-5 admin-payments-text-secondary">
                                        <Spinner animation="border" className="me-2" size="sm" /> Loading Transactions...
                                    </td>
                                </tr>
                            ) : logs.length > 0 ? (
                                logs.map((log, idx) => (
                                    <tr key={log.id} className="admin-payments-tbody-tr">
                                        <td className="px-4 admin-payments-text-secondary">{idx + 1}</td>
                                        <td className="fw-semibold text-white font-monospace">{log.application_id}</td>
                                        <td className="fw-semibold text-white">{log.user_name}</td>
                                        <td className="admin-payments-text-secondary">{log.email}</td>
                                        <td>{getGatewayBadge(log.gateway_provider)}</td>
                                        <td className="font-monospace admin-payments-text-secondary small">{log.order_id}</td>
                                        <td className="font-monospace admin-payments-text-secondary small">{log.payment_id}</td>
                                        <td className="fw-bold text-white">₹{Number(log.amount).toFixed(2)}</td>
                                        <td>
                                            <Badge bg={log.payment_status === 'SUCCESS' || log.payment_status === 'BOOKED' ? 'success' : log.payment_status === 'FAILED' ? 'danger' : 'warning'} className="px-2 py-1 rounded text-uppercase">
                                                {log.payment_status}
                                            </Badge>
                                        </td>
                                        <td className="text-muted">{log.created_at ? new Date(log.created_at).toLocaleString('en-IN') : '-'}</td>
                                                                                <td className="px-4 text-center">
                                            <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: "8px", width: "100%", margin: "0 auto" }}>
                                                {/* ✅ VIEW BUTTON: ROYAL BLUE */}
                                                <button
                                                    type="button"
                                                    style={{
                                                        background: "#2563eb",
                                                        backgroundColor: "#2563eb",
                                                        color: "#ffffff",
                                                        border: "none",
                                                        borderRadius: "6px",
                                                        width: "32px",
                                                        height: "32px",
                                                        display: "inline-flex",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                        cursor: "pointer",
                                                        padding: 0,
                                                        flexShrink: 0
                                                    }}
                                                    onClick={() => navigate(`/admin/payment-logs/${log.id}`)}
                                                    title="View Transaction Details"
                                                >
                                                    <Eye size={15} color="#ffffff" />
                                                </button>

                                                <Dropdown align="end">
                                                    <Dropdown.Toggle as="div" className="no-caret admin-payments-dropdown-toggle">
                                                        <MoreVertical size={16} className="text-secondary" />
                                                    </Dropdown.Toggle>
                                                    <Dropdown.Menu className="dropdown-menu-dark admin-payments-dropdown-menu">
                                                        <Dropdown.Item onClick={() => navigate(`/admin/payment-logs/${log.id}`)}>View Details</Dropdown.Item>
                                                        {log.payment_status !== 'SUCCESS' && log.payment_status !== 'BOOKED' && (
                                                            <Dropdown.Item className="text-danger" onClick={() => navigate(`/admin/payment-logs/${log.id}/approve`)}>
                                                                Manual Approve
                                                            </Dropdown.Item>
                                                        )}
                                                    </Dropdown.Menu>
                                                </Dropdown>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="11" className="text-center p-5 admin-payments-text-secondary">
                                        <ShieldAlert size={36} className="mb-2" />
                                        <p className="m-0">No transaction matched search conditions.</p>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </Table>
                </div>
            </Card>
        </div>
    );
};

export default AdminPaymentLogs;